import React, { type ReactNode, useCallback, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	CrossFlowInteractedUIActionSubjectIDs,
	CrossFlowViewedUIActionSubjectIDs,
} from '@atlassian/surface-analytics-specs';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { type EntryPointId } from './LoomCrossSellPopupCard/BaseLoomPopupCard';
import { useLoomCrossFlowRequestAccessCallback } from './useLoomCrossFlowRequestAccessCallback';
import { useLoomAdControlState } from './LoomAdControl/useLoomAdControlState';
import { useLoomCrossJoinUrl } from './useLoomCrossJoinUrl';

const sourceAdType = 'crossFlow';

export const CrossFlowMenuItem = ({
	buttonComponent: ButtonComponent,
	sourceActionSubjectId,
	entryPointId,
	sourceComponent,
	sourceContext,
}: {
	buttonComponent: (props: { onClickBeforeInit: () => void }) => ReactNode;
	// action subject ID of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	// a unique identifier for the entrypoint
	entryPointId: EntryPointId;
	sourceComponent: CrossFlowViewedUIActionSubjectIDs;
	sourceContext: CrossFlowInteractedUIActionSubjectIDs;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerCrossFlow = useLoomCrossFlowRequestAccessCallback({
		sourceComponent,
		sourceContext,
	});
	const { isInterestedInLoom } = useLoomAdControlState({
		touchpoint: entryPointId,
	});

	const onCrossFlowTriggerClick = useCallback(
		async (triggerName: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: triggerName,
					source: triggerName === 'tryNow' ? 'loomCrossFlowPopupCard' : sourceActionSubjectId,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
					},
				},
			}).fire();
			await triggerCrossFlow();
		},
		[createAnalyticsEvent, entryPointId, triggerCrossFlow, sourceActionSubjectId],
	);

	if (!isInterestedInLoom) {
		return null;
	}

	return (
		<ButtonComponent onClickBeforeInit={() => onCrossFlowTriggerClick(sourceActionSubjectId)} />
	);
};

export const CrossJoinMenuItem = ({
	buttonComponent: ButtonComponent,
	sourceActionSubjectId,
	entryPointId,
}: {
	buttonComponent: (props: {
		onClickBeforeInit: () => void;
		href: string | undefined;
		target: string | undefined;
		rel: string | undefined;
	}) => ReactNode;
	// action subject ID of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	// a unique identifier for the entrypoint
	entryPointId: EntryPointId;
	flags?: FlagsStateContainer;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const crossJoinHref = useLoomCrossJoinUrl();

	const onCrossJoinSuccess = useCallback(() => {
		experienceTracker.succeed({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			attributes: {
				sourceComponent: entryPointId,
				adType: sourceAdType,
			},
		});
	}, [entryPointId, experienceTracker]);

	const onCrossJoinTriggerClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: sourceActionSubjectId,
				source: sourceActionSubjectId,
				attributes: {
					adType: sourceAdType,
					entrypoint: entryPointId,
				},
			},
		}).fire();

		onCrossJoinSuccess();
	}, [createAnalyticsEvent, entryPointId, onCrossJoinSuccess, sourceActionSubjectId]);

	return (
		<ButtonComponent
			onClickBeforeInit={onCrossJoinTriggerClick}
			href={crossJoinHref}
			target="_blank"
			rel="noreferrer"
		/>
	);
};
